import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Loader2, Crown, Check, Info, Lock, Tag, Sparkles, Coins, Plane, BedDouble, Luggage, Stamp, Car, Headphones, ShieldCheck, Landmark } from "lucide-react";
import { formatINR, getRewardCashback, cashbackTitle, cashbackLabel } from "./VoucherCheckoutPanel";
import { fetchLoyaltyPrograms } from "../../store/loyaltySlices/loyaltyCardsSlice";
import { getUpgradeQuote } from "../../utils/membershipStatus";
import RequestConsiderationModal from "./modals/RequestConsiderationModal";
import InvitationStatusModal from "./modals/InvitationStatusModal";

// One accent per membership. Picked by program_id so a plan keeps its colour when the list re-sorts.
const ACCENTS = [
    { grad: "from-[#0B67FC] to-[#0B67FC]", tint: "from-[#0B67FC]/10", soft: "bg-[#0B67FC]/5", text: "text-[#083F94]", ring: "ring-[#0B67FC]/60", icon: "text-[#0B67FC]" },
    { grad: "from-violet-500 to-fuchsia-500", tint: "from-violet-100", soft: "bg-violet-50", text: "text-violet-800", ring: "ring-violet-400", icon: "text-violet-600" },
    { grad: "from-emerald-500 to-teal-500", tint: "from-emerald-100", soft: "bg-emerald-50", text: "text-emerald-800", ring: "ring-emerald-400", icon: "text-emerald-600" },
    { grad: "from-sky-500 to-indigo-500", tint: "from-sky-100", soft: "bg-sky-50", text: "text-sky-800", ring: "ring-sky-400", icon: "text-sky-600" },
    { grad: "from-rose-500 to-pink-500", tint: "from-rose-100", soft: "bg-rose-50", text: "text-rose-800", ring: "ring-rose-400", icon: "text-rose-600" },
];
const accentFor = (id) => ACCENTS[Math.abs(Number(id) || 0) % ACCENTS.length];

// Floating icon bubbles in the top-right corner of a plan card
const BUBBLE_POSITIONS = ["right-1 top-0 h-12 w-12", "right-14 top-10 h-11 w-11", "right-1 top-[4.75rem] h-14 w-14"];

function serviceIcon(name) {
    const n = String(name).toLowerCase();
    if (n.includes("flight")) return Plane;
    if (n.includes("hotel")) return BedDouble;
    if (n.includes("holiday") || n.includes("package") || n.includes("tour")) return Luggage;
    if (n.includes("visa")) return Stamp;
    if (n.includes("transfer") || n.includes("cab")) return Car;
    if (n.includes("support")) return Headphones;
    if (n.includes("insurance")) return ShieldCheck;
    if (n.includes("account")) return Landmark;
    return Check;
}

// Membership logo url. Adjust to the field your loyalty_program API actually returns.
const programLogo = (p) => p.logo || p.program_logo || p.card_logo || p.logo_url || null;

const planAmount = (p) => (p?.membership_type === "paid" ? Number(p.membership_amount) || 0 : 0);
/** Active plans a customer can take: paid or free. Legacy points-only programs have no membership_type. */
export function getMemberships(programs = []) {
    return programs.filter(
        (p) => Number(p.is_active) === 1 && (p.membership_type === "paid" || p.membership_type === "free")
    );
}

/** A reward tied to some membership (as opposed to one open to everyone) */
export const isMemberReward = (r) => (r.program_id != null && Number(r.program_id) > 0) || Boolean(r.program_name);

/** Store rewards that belong to the given membership */
export function getProgramRewards(rewards = [], program) {
    if (!program) return [];
    return rewards.filter((r) => {
        if (r.program_id != null && Number(r.program_id) > 0) return Number(r.program_id) === Number(program.program_id);
        return Boolean(r.program_name) && r.program_name === program.program_name;
    });
}

const bestPercent = (perks) =>
    Math.max(0, ...perks.filter((r) => r.dealtype === 0).map((r) => Number(r.custvalue) || 0));

function describeReward(r) {
    const isPct = r.dealtype === 0;
    const value = Number(r.custvalue) || 0;
    const cap = Number(r.maxdiscountvalue) || 0;
    const minOrder = Number(r.ordervalue) || 0;
    const notes = [];
    if (isPct && cap > 0) notes.push(`Up to ${formatINR(cap)}`);
    if (minOrder > 0) notes.push(`On orders above ${formatINR(minOrder)}`);
    const cashback = getRewardCashback(r);
    const hasDiscount = value > 0;
    if (cashback && hasDiscount) notes.push(cashbackLabel(cashback));
    else if (cashback?.type === "percent" && cashback.max > 0) notes.push(`Up to ${formatINR(cashback.max)}`);
    const title = hasDiscount || !cashback ? (isPct ? `${value}% OFF` : `${formatINR(value)} OFF`) : cashbackTitle(cashback);
    return { title, note: notes.join(" · ") };
}

function MembershipCard({ item, storeName, accent, current, locked, bestValue, isYourPlan, overrideLabel,alreadyMember, busy, disabled, showWelcomeCoin, onAction, onRequest, onHaveInvitation }) {
    const [termsOpen, setTermsOpen] = useState(false);
    const { program, perks, owned, quote } = item;
    const services = Array.isArray(program.services) ? program.services : [];
    const isPaid = program.membership_type === "paid";
    const amount = Number(program.membership_amount) || 0;
    const hasTerms = Boolean(program.terms_conditions);
    const logo = programLogo(program);
    const welcomeCoin = Number(program.welcome_coin) || 0;
    const visiblePerks = perks.slice(0, 2);
    const hiddenPerks = perks.length - visiblePerks.length;
    const bubbles = services.slice(0, 3);

    const [notice, setNotice] = useState(false);

    useEffect(() => {
        if (!notice) return;
        const t = setTimeout(() => setNotice(false), 2500);
        return () => clearTimeout(t);
    }, [notice]);

    const handleClick = () => {
        if (alreadyMember) {
            setNotice(true);
            return;
        }
        onAction();
    };

    let label = "Join Free";
    if (owned) label = "Use Membership";
    else if (isPaid) label = "Subscribe Now";
    if (overrideLabel) label = overrideLabel;
    if (alreadyMember) label = "Already a Member";

    return (
        <div
            className={`relative flex h-full flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm ${
                current ? `ring-2 ${accent.ring}` : ""
            }`}
        >
            {/* Tinted top: plan name, floating service icons, benefits list */}
            <div className={`relative bg-gradient-to-b ${accent.tint} via-white to-white px-5 pb-4 pt-5`}>
                {bubbles.length > 0 && (
                    <div className="pointer-events-none absolute right-3 top-4 h-40 w-32" aria-hidden="true">
                        {bubbles.map((service, i) => {
                            const Icon = serviceIcon(service);
                            return (
                                <span
                                    key={service}
                                    className={`absolute flex items-center justify-center rounded-full bg-white/90 shadow-sm ring-1 ring-black/5 ${BUBBLE_POSITIONS[i]}`}
                                >
                                    <Icon size={i === 2 ? 24 : 20} className={accent.icon} />
                                </span>
                            );
                        })}
                    </div>
                )}

                {(logo || locked || bestValue || current || isYourPlan) && (
                    <div className="relative mb-2 flex flex-wrap items-center gap-1.5">
                        {isYourPlan && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                                Your plan
                            </span>
                        )}
                        {logo && (
                            <img
                                src={logo}
                                alt={`${program.program_name} logo`}
                                className="mr-1 h-10 w-10 rounded-xl bg-white object-cover shadow-sm ring-1 ring-black/5"
                            />
                        )}
                        {locked && (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-black/5 ${accent.soft} ${accent.text}`}>
                                <Lock size={10} />
                                Invite only
                            </span>
                        )}
                        {bestValue && (
                            <span className="rounded-full bg-gray-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                                Best savings
                            </span>
                        )}
                        {current && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 shadow-2xs ring-1 ring-black/5">
                                <Check size={11} strokeWidth={3} />
                                Selected
                            </span>
                        )}
                    </div>
                )}

                <div className={`relative ${bubbles.length > 0 ? "pr-24" : ""}`}>
                    <h3
                        className={`bg-gradient-to-r bg-clip-text text-2xl font-extrabold uppercase leading-tight tracking-tight text-transparent ${accent.grad}`}
                    >
                        {program.program_name}
                    </h3>
                    {program.card_tagline && <p className="mt-1 text-sm font-semibold text-gray-700">{program.card_tagline}</p>}
                    {program.description && <p className="mt-1 line-clamp-2 text-xs text-gray-500">{program.description}</p>}

                    {services.length > 0 && (
                        <ul className="mt-4 space-y-3">
                            {services.map((service) => {
                                const Icon = serviceIcon(service);
                                return (
                                    <li key={service} className="flex items-start gap-3 text-[15px] leading-snug text-gray-800">
                                        <Icon size={18} className="mt-0.5 shrink-0 text-gray-500" />
                                        <span>{service}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {/* Rewards this plan unlocks at the current store */}
            <div className="flex-1 px-5 pb-4">
                <div className={`rounded-xl p-3 ${perks.length > 0 ? accent.soft : "bg-gray-50"}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        Member offers at {storeName || "this store"}
                    </p>
                    {perks.length > 0 ? (
                        <ul className="mt-2 space-y-2">
                            {visiblePerks.map((r) => {
                                const { title, note } = describeReward(r);
                                return (
                                    <li key={r.reward_id} className="flex items-start gap-2 text-sm">
                                        <Tag size={14} className={`mt-0.5 shrink-0 ${accent.icon}`} />
                                        <div className="min-w-0">
                                            <p>
                                                <span className="font-bold text-gray-900">{title}</span>
                                                {r.reward_name && <span className="text-gray-600"> · {r.reward_name}</span>}
                                            </p>
                                            {note && <p className="text-xs text-gray-500">{note}</p>}
                                        </div>
                                    </li>
                                );
                            })}
                            {hiddenPerks > 0 && (
                                <li className="pl-6 text-xs font-semibold text-gray-500">
                                    +{hiddenPerks} more {hiddenPerks === 1 ? "offer" : "offers"}
                                </li>
                            )}
                        </ul>
                    ) : (
                        <p className="mt-1 text-xs text-gray-500">No offers from this store on this plan yet.</p>
                    )}
                </div>

                {/* Welcome coins: only for a user's first membership purchase */}
                {showWelcomeCoin && welcomeCoin > 0 && !owned && (
                    <div className="mt-2 flex items-center gap-2 rounded-xl bg-[#0B67FC]/5 px-3 py-2 ring-1 ring-[#0B67FC]/20">
                        <Coins size={16} className="shrink-0 text-[#0B67FC]" />
                        <p className="text-xs font-semibold text-[#062E6C]">
                            Get {welcomeCoin.toLocaleString("en-IN")} welcome coins on your first membership
                        </p>
                    </div>
                )}
            </div>

            {/* Price + action (memberships are yearly only) */}
            <div className="mx-5 border-t border-gray-200" />
            <div className="flex items-center justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                    {owned ? (
                        <p className="inline-flex items-center gap-1.5 text-lg font-extrabold text-emerald-600">
                            <Check size={18} strokeWidth={3} />
                            Active member
                        </p>
                    ) : isPaid && quote && quote.credit > 0 ? (
                        <div>
                            <p className="text-2xl font-extrabold leading-tight text-gray-900">
                                {formatINR(quote.net)}
                                <span className="ml-1.5 text-base font-semibold text-gray-400 line-through">{formatINR(quote.price)}</span>
                            </p>
                            <p className="text-xs font-semibold text-emerald-600">
                                after {formatINR(quote.credit)} credit · {quote.daysLeft} days left on your plan
                            </p>
                        </div>
                    ) : isPaid ? (
                        <p className="text-2xl font-extrabold leading-tight text-gray-900">
                            {formatINR(amount)}
                            <span className="text-base font-bold text-gray-600">/12 months</span>
                        </p>
                    ) : (
                        <p className="text-2xl font-extrabold leading-tight text-emerald-600">Free</p>
                    )}
                </div>
                {!locked && (
    <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-disabled={alreadyMember || undefined}
        title={alreadyMember ? "Already a member" : undefined}
        className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-gradient-to-r px-6 py-3 text-sm font-bold text-white shadow-sm transition-all disabled:opacity-60 ${accent.grad} ${
            alreadyMember ? "cursor-not-allowed opacity-60" : "hover:brightness-95 active:scale-[0.98]"
        }`}
    >
        {busy && <Loader2 size={16} className="animate-spin" />}
        {label}
    </button>
)}
            </div>

            {/* Invite-only plans: ask to be considered, or continue with an invitation you already hold */}
            {locked && (
                <div className="-mt-1 grid grid-cols-2 gap-2 px-5 pb-4">
                    <button
                        type="button"
                        onClick={onRequest}
                        disabled={disabled}
                        className={`rounded-full border border-current px-3 py-2.5 text-xs font-bold transition-colors hover:bg-gray-50 disabled:opacity-60 sm:text-sm ${accent.text}`}
                    >
                        Request consideration
                    </button>
                    <button
                        type="button"
                        onClick={onHaveInvitation}
                        disabled={disabled}
                        className={`rounded-full bg-gradient-to-r px-3 py-2.5 text-xs font-bold text-white shadow-sm transition-all hover:brightness-95 active:scale-[0.98] disabled:opacity-60 sm:text-sm ${accent.grad}`}
                    >
                        I have an invitation
                    </button>
                </div>
            )}

            {(isPaid || hasTerms) && (
                <div className={`${accent.soft} ${accent.text} px-5 py-3`}>
                    <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                        <span>
                            {isPaid ? "Valid for 12 months" : "Free to join"}
                            {hasTerms ? " · Terms apply" : ""}
                        </span>
                        {hasTerms && (
                            <button
                                type="button"
                                aria-label="Terms and conditions"
                                aria-expanded={termsOpen}
                                onClick={() => setTermsOpen((v) => !v)}
                                className="shrink-0"
                            >
                                <Info size={18} />
                            </button>
                        )}
                    </div>
                    {termsOpen && (
                        <p className="mt-2 max-h-28 overflow-y-auto text-[11px] font-normal leading-relaxed text-gray-600">
                            {program.terms_conditions}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Full-page membership picker (yearly plans). Renders as a full-screen page over the current screen.
 * Reusable anywhere: it loads the programs itself (loyaltyCards slice).
 *
 * store:            optional, only used for the heading / "offers at <store>" wording
 * rewards:          the store's rewards; each plan shows the ones that belong to it
 * ownedProgramIds:  program_ids the user already has (shown as "Active member", button becomes "Use Membership")
 * selectedProgramId: plan to mark as "Selected" (e.g. the one chosen earlier)
 * onClose:          back arrow / Esc
 * onSelect(program, action): fired by a plan's button; action is "use" (owned), "join" (free) or "buy" (paid).
 *                   May return a promise - the button shows a spinner meanwhile.
 * onSkip:           optional; renders "Skip" in the top bar and the "continue without membership" button
 * isFirstMembership: true (default) while the user has never bought a membership; plans with welcome_coin > 0
 *                   then show the welcome-coins line. Pass false once the user's history is known.
 * onRequestConsideration(program): invite-only plans - "Request consideration" button
 * onHaveInvitation(program):       invite-only plans - "I have an invitation" button
 */
export default function MembershipPage({
    open,
    onClose,
    store,
    rewards = [],
    ownedProgramIds = [],
    selectedProgramId = null,
    // The user's own membership, independent of any per-store ownedProgramIds check:
    // currentProgramId/currentState come from utils/membershipStatus.getMembershipStatus(user).
    // state "active"  -> that plan shows "Your plan" + "Active member", every other plan shows "Upgrade"
    // state "expired" -> that plan shows "Your plan" + "Renew Membership", every other plan shows "Upgrade"
    // state "none"    -> normal Join Free / Subscribe Now labels
    currentProgramId = null,currentAmount: currentAmountProp = null,
    currentState = "none",
    // Unused value of the active plan from utils/membershipStatus.getMembershipCredit(user):
    // { amount, daysLeft, totalDays, perDay }. Taken off the price of an upgrade.
    credit = null,
    onSelect,
    onSkip,
    onRequestConsideration,
    onHaveInvitation,
    isFirstMembership = true,
    skipLabel = "Continue without membership",
    title = "Choose your plan",

}) {
    const dispatch = useDispatch();
    const { programs = [], loading } = useSelector((state) => state.loyaltyCards);
    const [submittingId, setSubmittingId] = useState(null);
    const [requestProgram, setRequestProgram] = useState(null); // invite-only plan whose request form is open
    const [invitationProgram, setInvitationProgram] = useState(null); // invite-only plan whose invitation status is open
    const inviteStatus = useSelector((state) => state.membershipInvite.statusByProgram);

    useEffect(() => {
        if (open && programs.length === 0) dispatch(fetchLoyaltyPrograms());
    }, [open, dispatch, programs.length]);

    // Esc goes back; lock the page behind while this screen is open
    useEffect(() => {
        if (!open) return;
        const handleKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", handleKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    const owned = new Set(ownedProgramIds.map(Number));
    const hasAnyMembership = currentState === "active" || currentState === "expired";
    const currentProgram = hasAnyMembership
    ? programs.find((p) => Number(p.program_id) === Number(currentProgramId))
    : null;
const currentAmount = currentProgram ? planAmount(currentProgram) : Number(currentAmountProp) || 0;

  const items = getMemberships(programs)
    .map((program) => {
        const perks = getProgramRewards(rewards, program);
        const isCurrent = currentProgramId != null && Number(currentProgramId) === Number(program.program_id);
        const isActiveCurrent = isCurrent && currentState === "active";

        const changeAction =
            !isCurrent && hasAnyMembership ? (planAmount(program) > currentAmount ? "upgrade" : "downgrade") : null;
        const overrideLabel =
            isCurrent && currentState === "expired"
                ? "Renew Membership"
                : changeAction === "upgrade"
                  ? "Upgrade"
                  : changeAction === "downgrade"
                    ? "Join"
                    : null;

        return {
            program,
            perks,
            bestPct: bestPercent(perks),
            owned: isActiveCurrent || owned.has(Number(program.program_id)),
            isCurrent,
            overrideLabel,
            changeAction,
            alreadyMember: isActiveCurrent,
            quote: changeAction === "upgrade" && credit?.amount > 0 ? getUpgradeQuote(program, credit) : null,
        };
    })
        // Active member: only plans worth more than theirs (upgrades). No / expired membership: every plan.
        .filter((i) => currentState !== "active" || i.changeAction === "upgrade")
        .sort(
            (a, b) =>
                Number(b.owned) - Number(a.owned) ||
                Number(b.perks.length > 0) - Number(a.perks.length > 0) ||
                b.bestPct - a.bestPct
        );

    const withSavings = items.filter((i) => i.bestPct > 0);
    const topPct = withSavings.length ? Math.max(...withSavings.map((i) => i.bestPct)) : 0;
    const bestValueId =
        withSavings.length > 1 ? withSavings.reduce((a, b) => (b.bestPct > a.bestPct ? b : a)).program.program_id : null;

    const isLocked = (i) =>
        i.program.invitation_required &&
        !i.owned &&
        inviteStatus[i.program.program_id]?.invite_status !== 1; // 1 = approved

    const handleAction = async (item) => {
        if (submittingId !== null || item.alreadyMember) return;
        const action =
    item.isCurrent && currentState === "expired"
        ? "renew"
        : item.changeAction
          ? item.changeAction // "upgrade" | "downgrade"
          : item.owned
            ? "use"
            : item.program.membership_type === "paid"
              ? "buy"
              : "join";
        setSubmittingId(item.program.program_id);
        try {
            await onSelect?.(item.program, action);
        } finally {
            setSubmittingId(null);
        }
    };

    // Approved invitation -> same flow as tapping the plan's button (buy / join / upgrade / downgrade)
    const continueWithInvitation = (program) => {
        const item = items.find((i) => Number(i.program.program_id) === Number(program.program_id));
        if (item) handleAction(item);
    };

    const showSkeleton = loading && items.length === 0;
    const gridClass =
        items.length === 1
            ? "mx-auto max-w-md"
            : items.length === 2
              ? "mx-auto max-w-3xl sm:grid-cols-2"
              : "sm:grid-cols-2 lg:grid-cols-3";

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Choose membership"
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-gray-50"
        >
            <div className="sticky top-0 z-10 border-b border-gray-100 bg-white/90 backdrop-blur">
                <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-3 py-2 sm:px-5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="-ml-2 inline-flex items-center gap-2 rounded-full px-2 py-1.5 text-gray-800 transition-colors hover:bg-gray-100"
                        aria-label="Back"
                    >
                        <ArrowLeft size={20} />
                        <span className="text-sm font-semibold">Back</span>
                    </button>
                    {onSkip && (
                        <button
                            type="button"
                            onClick={onSkip}
                            disabled={submittingId !== null}
                            className="text-sm font-semibold text-gray-600 transition-colors hover:text-gray-900 disabled:opacity-60"
                        >
                            Skip
                        </button>
                    )}
                </div>
            </div>

            <div className="relative overflow-hidden rounded-b-[2rem] bg-[#0B67FC] text-white">
                <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10" />
                <div className="pointer-events-none absolute -bottom-12 left-1/4 h-32 w-32 rounded-full bg-white/10" />
                <div className="relative mx-auto max-w-5xl px-3 pb-2 pt-2 sm:px-4">
                    <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/80">
                        <Crown size={14} />
                        Memberships
                    </div>
                    <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">
                        {store?.store_name ? `Save more at ${store.store_name}` : "Save more with a membership"}
                    </h1>
                    <p className="mt-1 max-w-xl text-sm text-white/85">
                        {onSkip ? "" : "A membership is required to continue. "}
                        {hasAnyMembership
                            ? currentState === "expired"
    ? "Renew your plan, or switch to a different one."
    : "Upgrade to a higher plan to unlock even better offers."
                            : "Pick a yearly plan to unlock member-only rewards."}
                    </p>
                    {topPct > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-black/20 px-3 py-1.5 text-xs font-bold">
                            <Sparkles size={13} />
                            Members save up to {topPct}% here
                        </div>
                    )}
                </div>
            </div>

            <div className="mx-auto max-w-5xl px-4 pb-12 pt-3 sm:px-6">
                {currentState === "active" && credit?.amount > 0 && (
                    <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                            <Sparkles size={18} className="text-emerald-600" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-extrabold text-emerald-800">Good news! We've got you covered.</p>
                            <p className="mt-0.5 text-sm text-emerald-700">
                                Your {currentProgram?.program_name || "current"} membership still has{" "}
                                <span className="font-bold">{credit.daysLeft} {credit.daysLeft === 1 ? "day" : "days"}</span> left,
                                so we'll give you <span className="font-bold">{formatINR(credit.amount)}</span> to use towards your upgrade.
                            </p>
                        </div>
                    </div>
                )}
                <div className="mb-3 flex items-center gap-2">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">{title}</h2>
                    <span className="h-px flex-1 bg-gray-200" />
                </div>

                {showSkeleton ? (
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {[0, 1, 2].map((n) => (
                            <div key={n} className="h-96 animate-pulse rounded-3xl bg-gray-200/70" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="mx-auto max-w-md rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                        <Crown className="mx-auto mb-2 text-gray-300" size={28} />
                        <p className="text-sm font-medium text-gray-500">
                            {currentState === "active"
                                ? "You're already on our highest membership. There's nothing to upgrade to."
                                : "No memberships available right now."}
                        </p>
                    </div>
                ) : (
                    <div className={`grid gap-5 ${gridClass}`}>
                        {items.map((item) => (
                            <MembershipCard
                                key={item.program.program_id}
                                item={item}
                                storeName={store?.store_name}
                                accent={accentFor(item.program.program_id)}
                                current={Number(selectedProgramId) === Number(item.program.program_id)}
                                isYourPlan={item.isCurrent}
                                overrideLabel={item.overrideLabel}
                                locked={isLocked(item)}
                                bestValue={bestValueId === item.program.program_id}
                                busy={submittingId === item.program.program_id}
                                disabled={submittingId !== null}
                                showWelcomeCoin={isFirstMembership}
                                alreadyMember={item.alreadyMember}
                                onAction={() => handleAction(item)}
                                onRequest={() => {
                                    setRequestProgram(item.program);
                                    onRequestConsideration?.(item.program);
                                }}
                                onHaveInvitation={() => {
                                    setInvitationProgram(item.program);
                                    onHaveInvitation?.(item.program);
                                }}
                                
                            />
                        ))}
                    </div>
                )}

                {onSkip && (
                    <div className="mt-8 text-center">
                        <button
                            type="button"
                            onClick={onSkip}
                            disabled={submittingId !== null}
                            className="rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-60"
                        >
                            {skipLabel}
                        </button>
                    </div>
                )}
            </div>

            <InvitationStatusModal
                open={Boolean(invitationProgram)}
                program={invitationProgram}
                onClose={() => setInvitationProgram(null)}
                onContinue={continueWithInvitation}
                onRequest={(program) => setRequestProgram(program)}
            />
            <RequestConsiderationModal
                open={Boolean(requestProgram)}
                program={requestProgram}
                onClose={() => setRequestProgram(null)}
            />
        </div>
    );
}